import { supabase } from '@/lib/supabase';
import { createKnowledgeFolder, listKnowledgeFolders, uploadKnowledgeFileDocument, uploadKnowledgeInlineDocument } from '@/lib/knowledge';
import type { ChatSource } from '@shared/types/chat-sources';

/**
 * D4 — a file dropped in the side chat goes to the person's own folder
 * « Fichiers du chat » (personal: no one else reads it) and is cited at once;
 * an answer can be put away in a folder the person may write to.
 */

export const CHAT_FILES_FOLDER = 'Fichiers du chat';
/** What the upload service reads (text extracted from PDF and Word, plain text, Markdown). */
export const CHAT_FILE_ACCEPT = /\.(pdf|docx|txt|md|markdown)$/i;

const identity = async (): Promise<{ userId: string; tenantId: string; isManager: boolean }> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Session expirée : reconnectez-vous.');
  const { data } = await supabase.from('tenant_members').select('tenant_id, tenant_role').eq('user_id', user.id).maybeSingle();
  if (!data) throw new Error('Espace introuvable.');
  return { userId: user.id, tenantId: String(data.tenant_id), isManager: data.tenant_role === 'manager' };
};

const titleOf = (fileName: string): string => fileName.replace(/\.[^.]+$/, '').trim() || fileName;

export const uploadChatFile = async (file: File): Promise<ChatSource> => {
  if (!CHAT_FILE_ACCEPT.test(file.name)) throw new Error('Formats lus : PDF, Word, texte et Markdown.');
  const { userId, tenantId } = await identity();
  const folders = await listKnowledgeFolders();
  const folder = folders.find((item) => item.kind === 'personal' && item.owner_user_id === userId && item.name === CHAT_FILES_FOLDER)
    ?? await createKnowledgeFolder({ name: CHAT_FILES_FOLDER, kind: 'personal', tenantId, userId });
  const title = titleOf(file.name);
  const { document } = await uploadKnowledgeFileDocument({ folderId: folder.id, title, file, userId });
  return { ref: { kind: 'knowledge_document', documentId: document.id }, label: document.title };
};

/** Folders an answer can go to: my personal ones, and the shared ones for a manager (same rule as the RLS). */
export const listWritableFolders = async (): Promise<Array<{ id: string; name: string }>> => {
  const [{ userId, isManager }, folders] = await Promise.all([identity(), listKnowledgeFolders()]);
  return folders
    .filter((folder) => (folder.kind === 'personal' ? folder.owner_user_id === userId : isManager))
    .map((folder) => ({ id: folder.id, name: folder.name }));
};

export const saveAnswerToFolder = async (params: { folderId: string; title: string; content: string }): Promise<string> => {
  const { document } = await uploadKnowledgeInlineDocument(params);
  return document.id;
};
